import { useState, useRef, useEffect } from 'react';
import firebaseService from '../../../firebase/firebaseService';
import useAnalytics from '../../../hooks/useAnalytics';
import useSendVerificationEmailMutation from './useSendVerificationEmailMutation';
import useSendPasswordResetMutation from './useSendPasswordResetMutation';

const RESEND_COOLDOWN_SECONDS = 60;

const useAuthPage = () => {
  const { trackAnonymousEvent } = useAnalytics();
  const { mutateAsync: sendVerificationEmail } =
    useSendVerificationEmailMutation();
  const { mutateAsync: sendPasswordReset } = useSendPasswordResetMutation();
  const [isRegister, setIsRegister] = useState(false);
  const emailTracked = useRef(false);
  const passwordTracked = useRef(false);

  useEffect(() => {
    emailTracked.current = false;
    passwordTracked.current = false;
  }, [isRegister]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const resendTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  useEffect(() => () => clearTimeout(resendTimeout.current), []);

  const startResendCooldown = () => {
    setResendDisabled(true);
    resendTimeout.current = setTimeout(
      () => setResendDisabled(false),
      RESEND_COOLDOWN_SECONDS * 1000,
    );
  };

  const handlePrimaryButtonPress = async () => {
    if (isSubmitting || !email || !password) return;
    setIsSubmitting(true);
    try {
      if (isRegister) {
        trackAnonymousEvent({ type: 'clicked_join_now' });
        const user = await firebaseService.createUserWithEmailAndPassword(
          email,
          password,
        );
        if (user && !user.emailVerified) {
          await sendVerificationEmail();
          startResendCooldown();
          window.alert(
            'Please check your inbox and verify your email address. ' +
              'It may take a few minutes to arrive — also check your spam folder.',
          );
        }
      } else {
        await firebaseService.signInWithEmailAndPassword(email, password);
      }
    } catch (error) {
      console.error('Authentication error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      alert('Please enter your email address first.');
      return;
    }
    try {
      await sendPasswordReset(email);
      window.alert(
        'Password Reset Email Sent: Check your email for instructions to reset your password.',
      );
    } catch (error) {
      console.error('Password reset error:', error);
      window.alert('Something went wrong. Please try again later.');
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      trackAnonymousEvent({ type: 'clicked_google_sign_in' });
      await firebaseService.signInWithGoogle();
    } catch (error) {
      console.error('Google Sign-In error:', error);
    }
  };

  const handleVerifyEmail = async () => {
    if (resendDisabled) return;
    try {
      await sendVerificationEmail();
      startResendCooldown();
      window.alert(
        'Verification email sent. It may take a few minutes to arrive — ' +
          'please check your inbox and spam folder.',
      );
    } catch (error) {
      console.error('Verification email error:', error);
      window.alert('Something went wrong. Please try again later.');
    }
  };

  const handleEmailBlur = () => {
    if (isRegister && email && !emailTracked.current) {
      emailTracked.current = true;
      trackAnonymousEvent({ type: 'register_email_filled' });
    }
  };

  const handlePasswordBlur = () => {
    if (isRegister && password && !passwordTracked.current) {
      passwordTracked.current = true;
      trackAnonymousEvent({ type: 'register_password_filled' });
    }
  };

  const handleTabChange = (
    _: React.ChangeEvent<unknown>,
    newValue: boolean,
  ) => {
    setIsRegister(newValue);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return {
    isRegister,
    setIsRegister,
    email,
    setEmail,
    password,
    setPassword,
    handlePrimaryButtonPress,
    isSubmitting,
    handleForgotPassword,
    handleGoogleSignIn,
    handleTabChange,
    handleVerifyEmail,
    handleEmailBlur,
    handlePasswordBlur,
    showPassword,
    togglePasswordVisibility,
    resendDisabled,
  };
};

export default useAuthPage;
